import { Icon } from '@tremor/react';
import { FC, InputHTMLAttributes, useRef, useState } from 'react';
import s from './SingleItem.module.scss';
import { CheckmarkIcon, PriceTagIcon, PriceTagIconWhite } from '@/assets';
import { ClipLoader } from 'react-spinners';
import { RiAlarmWarningLine, RiErrorWarningLine } from '@remixicon/react';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';
import { ConnectorsService } from '@/services/connectorsService';
import classNames from 'classnames';
import { deleteAdAccountCloudFunction } from '@/firebase/init';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  isSelected: boolean;
  displayIcon?: boolean;
  isLoading?: boolean;
  status?: string;
  accountId?: string;
  connectorId?: string;
  annotation?: string;
}

type ConditionalProps =
  | { selectedIcon?: never; unselectedIcon?: never }
  | { selectedIcon: string; unselectedIcon: string };

export const SingleItem: FC<ConditionalProps & Props> = ({
  children,
  className,
  isSelected,
  displayIcon = true,
  selectedIcon,
  unselectedIcon,
  isLoading,
  disabled,
  status,
  accountId,
  connectorId,
  onChange,
  annotation,
  ...rest
}) => {
  const displaySelectedIcon = selectedIcon || PriceTagIconWhite;
  const displayUnselectedIcon = unselectedIcon || PriceTagIcon;
  const workspaceId = useWorkspaceIdFromParams();
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isFixingAnnotation, setIsFixingAnnotation] = useState(false);

  const styles = [s.tagButton];

  if (isSelected && !isLoading) {
    styles.push(s.tagButtonSelected);
  }

  if (className) styles.push(className);

  const acknowledgeError = async () => {
    if (!accountId || !connectorId || !workspaceId || !status) {
      console.error('Missing required data to acknowledge error');
      return;
    }
    await ConnectorsService.acknowledgeAdAccountError(
      workspaceId,
      connectorId,
      accountId,
      status,
    );
  };

  const deleteUnavailableAdAccount = async () => {
    if (!accountId || !connectorId || !workspaceId) {
      console.error('Missing required data to delete ad account');
      return;
    }
    setIsFixingAnnotation(true);
    try {
      await deleteAdAccountCloudFunction({
        workspaceId,
        connectorId,
        accountIds: [accountId],
      });
    } catch (error) {
      console.error('Error deleting ad account', error);
      setIsFixingAnnotation(false);
    }
  };

  const isFailure = status && status.includes('FAILURE');

  return (
    <li className={s.singleItemLi}>
      <label
        className={classNames(styles, {
          [s.disabled]: disabled || isLoading || !!annotation,
        })}
      >
        <input
          checked={isSelected}
          type="checkbox"
          onChange={(e) => {
            const selection = window.getSelection();
            if (
              selection &&
              selection.toString().length > 0 &&
              selection.anchorNode?.parentElement?.isEqualNode(spanRef.current)
            ) {
              e.stopPropagation();
              return;
            }
            onChange?.(e);
          }}
          className={classNames(s.hiddenCheckbox, {
            [s.hiddenCheckboxSelected]: isSelected,
            [s.hiddenCheckboxUnselected]: !isSelected,
          })}
          disabled={disabled || isLoading || !!annotation}
          {...rest}
        />
        {displayIcon && (
          <img
            height={16}
            width={16}
            src={isSelected && !isLoading ? displaySelectedIcon : displayUnselectedIcon}
            alt=""
          />
        )}
        <span ref={spanRef} style={{ userSelect: 'text' }}>
          {children}
        </span>
        {isLoading && !isFailure ? (
          <ClipLoader />
        ) : (
          <>
            {status === 'ACCOUNT_SUCCESS' && <span>CONNECTED</span>}
            <img
              style={{ marginLeft: 'auto' }}
              src={CheckmarkIcon}
              alt="checkmark icon"
            />
          </>
        )}
      </label>
      {annotation && !isLoading && !isFixingAnnotation ? (
        <Icon
          onClick={deleteUnavailableAdAccount}
          aria-label="button"
          style={{
            color: 'red',
            cursor: 'pointer',
            position: 'absolute',
            top: '50%',
            right: '0',
            transform: 'translate(-50%, -50%)',
          }}
          icon={RiAlarmWarningLine}
          tooltip="Your account no longer has access to this ad account. To modify it, please regain access to the ad account. Click this icon to remove this ad account."
        />
      ) : null}
      {!annotation && status && status.includes('FAILURE') ? (
        <Icon
          onClick={acknowledgeError}
          aria-label="button"
          style={{
            color: 'red',
            cursor: 'pointer',
            position: 'absolute',
            top: '50%',
            right: '0',
            transform: 'translate(-50%, -50%)',
          }}
          icon={RiErrorWarningLine}
          tooltip="There was an error updating this ad account. Click this icon to acknowledge the error."
        />
      ) : null}
    </li>
  );
};
